import React, { ChangeEvent, MouseEvent, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageWithHeader, Content, Progress } from '@backstage/core-components';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { Typography } from '@material-ui/core';
import { useProduct } from './hooks';
import {
  API_EXPLORER_PATH,
  ApiExplorerLocationState,
  ProductDoc,
} from './types';
import {
  ClientCredentialsContent,
  ClientCredentialsLink,
  DefinitionPageTag,
  DefinitionPageTitle,
  SwaggerWrapper,
} from './ApiDefinitionPage.styles';
import { FlexBox } from '../global/FlexBox';
import { ApiDefinitionTabLayout } from './components/ApiDefinitionTabLayout/ApiDefinitionTabLayout';
import { MarkdownDocTab } from './components/MarkdownDocTab';
import { StyledTab, StyledTabs, TabsWrapper } from '../DocsV2/styles';

interface ApiDefinitionTabConfig {
  id: string;
  label: string;
  kind: 'definition' | 'doc' | 'clientCredentials';
  doc?: ProductDoc;
}

const DEFINITION_TAB_ID = 'definition';
const CLIENT_CREDENTIALS_TAB_ID = 'client-credentials';

function createMarkdownTabConfig(doc: ProductDoc): ApiDefinitionTabConfig {
  return {
    id: `doc-${doc.id}`,
    label: doc.title,
    kind: 'doc',
    doc,
  };
}

function buildApiDefinitionTabs(docs: ProductDoc[]): ApiDefinitionTabConfig[] {
  return [
    {
      id: DEFINITION_TAB_ID,
      label: 'Definition',
      kind: 'definition',
    },
    ...docs.map(createMarkdownTabConfig),
    {
      id: CLIENT_CREDENTIALS_TAB_ID,
      label: 'Client Credentials',
      kind: 'clientCredentials',
    },
  ];
}

function ApiDefinitionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: ApiExplorerLocationState | null };
  const { data: product, isLoading: isLoadingProduct } = useProduct(id ?? '');
  const tabs = React.useMemo(
    () => buildApiDefinitionTabs(product?.docs ?? []),
    [product?.docs],
  );
  const [currentTab, setCurrentTab] = React.useState<string>(DEFINITION_TAB_ID);

  function handleBack() {
    navigate({ pathname: API_EXPLORER_PATH, search: state?.fromSearch ?? '' });
  }

  function handleSettingsLinkClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    navigate('/settings/client-credentials');
  }

  function handleTabChange(_event: ChangeEvent<{}>, newValue: string) {
    setCurrentTab(newValue);
  }

  useEffect(() => {
    const isTabAvailable = tabs.some(tab => tab.id === currentTab);

    if (!isTabAvailable) {
      setCurrentTab(DEFINITION_TAB_ID);
    }
  }, [currentTab, tabs]);

  function renderTabContent(tab: ApiDefinitionTabConfig | undefined) {
    if (!tab) {
      return null;
    }

    if (tab.kind === 'definition') {
      return (
        <ApiDefinitionTabLayout onBack={handleBack}>
          <SwaggerWrapper>
            <SwaggerUI url={product?.specs?.[0]?.url} />
          </SwaggerWrapper>
        </ApiDefinitionTabLayout>
      );
    }

    if (tab.kind === 'doc') {
      return (
        <ApiDefinitionTabLayout onBack={handleBack}>
          <MarkdownDocTab sourceUrl={tab.doc?.url ?? null} />
        </ApiDefinitionTabLayout>
      );
    }

    return (
      <ApiDefinitionTabLayout onBack={handleBack} variant="clientCredentials">
        <ClientCredentialsContent>
          <Typography variant="h5">Customer Portal Credentials</Typography>
          {product?.clientCredentials &&
            product?.clientCredentials?.length > 0 && (
              <Typography variant="body1" color="textPrimary">
                The credentials required for this API are shown below:
              </Typography>
            )}
          <FlexBox direction="column" sx={{ alignItems: 'start' }}>
            {product?.clientCredentials?.map(credential => (
              <Typography variant="body1" color="textPrimary" key={credential}>
                {credential}
              </Typography>
            ))}
          </FlexBox>
          <Typography variant="body1" color="textPrimary">
            Respective credential values are available in{' '}
            <strong>
              Settings {'>'}{' '}
              <ClientCredentialsLink
                href="/settings/client-credentials"
                onClick={handleSettingsLinkClick}
              >
                Client Credentials
              </ClientCredentialsLink>
            </strong>{' '}
            (for example, Client ID and Client Secret).
          </Typography>
        </ClientCredentialsContent>
      </ApiDefinitionTabLayout>
    );
  }

  const activeTab = tabs.find(tab => tab.id === currentTab);

  if (isLoadingProduct) {
    return <Progress />;
  }

  return (
    <PageWithHeader
      themeId="apis"
      pageTitleOverride={product?.title}
      title={
        <FlexBox sx={{ gridColumnGap: 16, justifyContent: 'start' }}>
          <DefinitionPageTitle variant="h4">
            {product?.title}
          </DefinitionPageTitle>
          <DefinitionPageTag
            variant="outlined"
            color="primary"
            label={product?.tag}
          />
        </FlexBox>
      }
    >
      <Content>
        <TabsWrapper>
          <StyledTabs
            textColor="primary"
            indicatorColor="primary"
            value={currentTab}
            onChange={handleTabChange}
          >
            {tabs.map(tab => (
              <StyledTab key={tab.id} value={tab.id} label={tab.label} />
            ))}
          </StyledTabs>
        </TabsWrapper>
        {renderTabContent(activeTab)}
      </Content>
    </PageWithHeader>
  );
}

export default ApiDefinitionPage;
